import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkle, LockKey, Eye, EyeSlash, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, saveAuth } from "@/lib/api";
import { hasPlatformAccess } from "@/lib/access";
import { SiloArtCanvasFor } from "@/components/SiloArtLayer";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  // Jun 2026 — 2FA: when the account has two-factor on, the first login call
  // returns { requires_2fa, mfa_token } and we swap to a code-entry step.
  const [mfaToken, setMfaToken] = useState(null);
  const [code, setCode] = useState("");
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = searchParams.get("next");

  // Silo Art V2 on the sign-in page: dress it with the last silo the visitor
  // was in (its public look), since there's no logged-in leader yet. Only
  // themes when that silo's creator has V2 turned on.
  const [themeSilo, setThemeSilo] = useState(null);
  useEffect(() => {
    let slug = null;
    try { slug = localStorage.getItem("mysilo_theme_slug"); } catch { /* storage off */ }
    if (!slug) return;
    api.get(`/s/${slug}`).then((r) => { if (r?.data?.silo_art_v2) setThemeSilo(r.data); }).catch(() => {});
  }, []);

  // Jun 2026 — if the API layer bounced us here because a stored token went
  // stale (every call was 401'ing), explain why instead of a silent redirect.
  useEffect(() => {
    try {
      if (sessionStorage.getItem("mysilo_session_expired")) {
        sessionStorage.removeItem("mysilo_session_expired");
        toast.message("Your session expired — please sign in again.");
      }
    } catch { /* storage off */ }
  }, []);

  const finishLogin = (token, user) => {
    saveAuth(token, user);
    toast.success(`Welcome back, ${user.name}.`);
    if (nextPath && nextPath.startsWith("/")) { nav(nextPath); return; }
    const u = user || {};
    const isOperator = hasPlatformAccess(u) || !!u.is_affiliate;
    nav(isOperator ? "/dashboard" : "/fan-portal");
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/auth/login", { email, password });
      if (r.data.requires_2fa) {
        setMfaToken(r.data.mfa_token);
        setLoading(false);
        toast.message("Enter your 2FA code to finish signing in.");
        return;
      }
      finishLogin(r.data.token, r.data.user);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  const verify2fa = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/auth/login/2fa", { mfa_token: mfaToken, code });
      finishLogin(r.data.token, r.data.user);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid code");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#FDFCF8] flex flex-col relative isolate overflow-hidden" data-testid="login-page">
      {themeSilo && <SiloArtCanvasFor silo={themeSilo} face="center" />}
      <nav className="border-b hairline">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#1C1B1A] text-[#FDFCF8] flex items-center justify-center"><Sparkle size={18} weight="duotone" /></div>
            <span className="font-serif-display text-xl">My Silo</span>
          </Link>
          <Link to="/signup" className="text-sm text-[#6B6A68] hover:text-[#1C1B1A]">No account? <span className="underline">Sign up</span></Link>
        </div>
      </nav>

      <main className="siloart-copy flex-1 flex items-center justify-center px-6 py-12">
        {!mfaToken ? (
          <motion.form initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} onSubmit={submit}
            className="w-full max-w-md bg-white border hairline rounded-xl shadow-sm p-8">
            <h1 className="font-serif-display text-3xl text-[#1C1B1A]">Welcome back.</h1>
            <p className="text-[#6B6A68] text-sm mt-1">Sign in to manage your silos.</p>

            <label className="block text-sm text-[#6B6A68] mt-6 mb-1.5">Email</label>
            <input data-testid="login-email" type="email" required value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-white border hairline rounded-md px-4 py-3 focus:outline-none focus:ring-1 focus:ring-[#4A5D4E] focus:border-[#4A5D4E]" placeholder="you@email.com" />

            <label className="block text-sm text-[#6B6A68] mt-4 mb-1.5">Password</label>
            <div className="relative">
              <input data-testid="login-password" type={showPw ? "text" : "password"} required value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-white border hairline rounded-md px-4 py-3 pr-12 focus:outline-none focus:ring-1 focus:ring-[#4A5D4E] focus:border-[#4A5D4E]" placeholder="••••••••" />
              <button type="button" onClick={() => setShowPw((s) => !s)} data-testid="login-password-toggle"
                aria-label={showPw ? "Hide password" : "Show password"}
                className="absolute inset-y-0 right-0 flex items-center px-3 text-[#6B6A68] hover:text-[#1C1B1A]">
                {showPw ? <EyeSlash size={18} weight="duotone" /> : <Eye size={18} weight="duotone" />}
              </button>
            </div>

            <div className="text-right mt-2">
              <Link to="/forgot-password" data-testid="login-forgot-password" className="text-xs text-[#4A5D4E] hover:underline">
                Forgot password?
              </Link>
            </div>

            <button type="submit" disabled={loading} data-testid="login-submit"
              className="mt-6 w-full bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] px-6 py-3 rounded-md transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
              <LockKey size={16} weight="duotone" /> {loading ? "Signing in…" : "Sign in"}
            </button>
          </motion.form>
        ) : (
          <motion.form initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} onSubmit={verify2fa}
            data-testid="login-2fa-form"
            className="w-full max-w-md bg-white border hairline rounded-xl shadow-sm p-8">
            <div className="w-10 h-10 rounded-md bg-[#F0F1EE] text-[#4A5D4E] flex items-center justify-center mb-3">
              <ShieldCheck size={20} weight="duotone" />
            </div>
            <h1 className="font-serif-display text-3xl text-[#1C1B1A]">Two-factor check.</h1>
            <p className="text-[#6B6A68] text-sm mt-1">Enter the 6-digit code from your authenticator app — or a recovery code.</p>

            <label className="block text-sm text-[#6B6A68] mt-6 mb-1.5">Code</label>
            <input data-testid="login-2fa-code" type="text" inputMode="text" autoFocus required value={code}
              onChange={(e) => setCode(e.target.value)}
              className="w-full bg-white border hairline rounded-md px-4 py-3 tracking-widest text-center focus:outline-none focus:ring-1 focus:ring-[#4A5D4E] focus:border-[#4A5D4E]"
              placeholder="123 456" />

            <button type="submit" disabled={loading || !code.trim()} data-testid="login-2fa-submit"
              className="mt-6 w-full bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] px-6 py-3 rounded-md transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
              <LockKey size={16} weight="duotone" /> {loading ? "Verifying…" : "Verify & sign in"}
            </button>
            <button type="button" onClick={() => { setMfaToken(null); setCode(""); }} data-testid="login-2fa-back"
              className="mt-3 w-full text-xs text-[#6B6A68] hover:text-[#1C1B1A]">
              ← Back to sign in
            </button>
          </motion.form>
        )}
      </main>
    </div>
  );
}
